import { Skeleton, Stack, TableCell, TableRow, Typography } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import React, { useMemo } from "react"

import { SpawnAgentDialog } from "./SpawnAgentDialog"
import { Token } from "../EntityPage/EntityPortfolioAgents"
import { getPrimaryName } from "@/api/arns-api"
import { PortfolioAgent, readAgentInfo } from "@/api/portfolio-agents-api"
import { useTokenPrices } from "@/api/portfolio-api"
import CloneAgentButton from "@/components/CloneAgentButton"
import { IdBlock } from "@/components/IdBlock"
import { PortfolioAgentIcon } from "@/components/PortfolioAgentIcon"
import { NumberFont } from "@/components/RootLayout/fonts"
import { UsdAmountBlock } from "@/components/UsdAmountBlock"
import { useTokenInfo } from "@/hooks/use-token-info"
import { truncateId } from "@/utils/data-utils"
import { parseBigIntAsNumber } from "@/utils/format"

export function PortfolioAgentsTableRow(props: { row: PortfolioAgent }) {
  const { row: agent } = props

  const { data: agentInfo } = useQuery({
    queryKey: ["agent_info", agent.id],
    queryFn: async () => {
      // console.log("Fetching Agent Info", agent.id)
      const res = await readAgentInfo(agent.id)
      console.log("onAgentInfo", agent.id, res)
      return res
    },
    enabled: !!agent.id,
  })

  const [quoteTokenInfo] = useTokenInfo(agent.quote_token)
  const [tokenPrices] = useTokenPrices()

  const [pnl24h, pnl7d, pnl30d, pnlAllTime] = useMemo(() => {
    if (!agentInfo || !quoteTokenInfo || agent.pnl_all_time_abs === undefined)
      return [null, null, null, null]

    if (agent.created_at_ts >= 1739200020000) {
      return [
        Number(parseBigIntAsNumber(agent.pnl_24h_abs, quoteTokenInfo.denomination)),
        Number(parseBigIntAsNumber(agent.pnl_7d_abs, quoteTokenInfo.denomination)),
        Number(parseBigIntAsNumber(agent.pnl_30d_abs, quoteTokenInfo.denomination)),
        Number(parseBigIntAsNumber(agent.pnl_all_time_abs, quoteTokenInfo.denomination)),
      ]
    }

    if (!tokenPrices) return [null, null, null, null]

    const price = tokenPrices[quoteTokenInfo.id].price
    return [
      Number(parseBigIntAsNumber(agent.pnl_24h_abs, quoteTokenInfo.denomination)) * price,
      Number(parseBigIntAsNumber(agent.pnl_7d_abs, quoteTokenInfo.denomination)) * price,
      Number(parseBigIntAsNumber(agent.pnl_30d_abs, quoteTokenInfo.denomination)) * price,
      Number(parseBigIntAsNumber(agent.pnl_all_time_abs, quoteTokenInfo.denomination)) * price,
    ]
  }, [agent, agentInfo, tokenPrices, quoteTokenInfo])

  const netWorth = useMemo(() => {
    if (!agentInfo || !tokenPrices || !quoteTokenInfo) return null
    if (!agentInfo.Holdings || !agentInfo.Holdings.entries) return 0

    const totalValueQuoteToken = parseFloat(agentInfo.Holdings.realTotalValue)
    const totalValueQuoteTokenDecimalForm =
      totalValueQuoteToken / Math.pow(10, quoteTokenInfo.denomination)
    const quoteTokenPriceInfo = tokenPrices[quoteTokenInfo.id]
    if (!quoteTokenPriceInfo || !quoteTokenPriceInfo.price) {
      return 0
    }
    const quoteTokenPriceUSD = quoteTokenPriceInfo.price
    const totalValue = totalValueQuoteTokenDecimalForm * quoteTokenPriceUSD

    return totalValue
  }, [agentInfo, tokenPrices, quoteTokenInfo])

  const [open, setOpen] = React.useState(false)

  const { data: arnsName } = useQuery({
    queryKey: ["arns_name", agent.portfolio_owner],
    queryFn: () => getPrimaryName(agent.portfolio_owner),
    enabled: !!agent.portfolio_owner,
  })

  return (
    <>
      <TableRow>
        <TableCell variant="text">
          <Stack direction="row" gap={2} alignItems="center">
            <PortfolioAgentIcon
              width={18}
              style={{
                transform: `rotate(${Math.random() * 360}deg)`,
              }}
            />
            <Stack>
              <IdBlock
                label={agentInfo?.["Agent-Name"] || truncateId(agent.id)}
                value={agent.id}
                href={`/entity/${agent.id}`}
                hideCopyToClipboard
              />
              <Typography variant="caption" color="text.secondary">
                <Stack direction="row" gap={0.5} alignItems="center">
                  owned by{" "}
                  <IdBlock
                    label={arnsName ? `${arnsName}.arns` : truncateId(agent.portfolio_owner)}
                    value={agent.portfolio_owner}
                    href={`/entity/${agent.portfolio_owner}`}
                    hideCopyToClipboard
                  />
                </Stack>
              </Typography>
            </Stack>
          </Stack>
        </TableCell>
        <TableCell>
          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={-2}
            sx={{
              "& > div > *": {
                border: (theme) => `2px solid ${theme.palette.paperAlt}`,
              },
            }}
          >
            {agent.token_processes.map((token) => (
              <Token key={token} tokenId={token} />
            ))}
          </Stack>
        </TableCell>
        <TableCell align="right" variant="number">
          {netWorth !== null ? (
            <Typography
              // color="accent.main"
              fontFamily={NumberFont}
              fontWeight={700}
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
              }}
            >
              <UsdAmountBlock amount={netWorth} />
            </Typography>
          ) : (
            <Skeleton width={80} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
        <TableCell align="right" variant="number">
          <UsdAmountBlock amount={agent.volume_all_time} />
        </TableCell>
        <TableCell align="right" variant="number">
          {pnl24h !== null ? (
            <span
              style={{
                color:
                  pnl24h === 0
                    ? ""
                    : pnl24h > 0
                      ? "var(--mui-palette-success-main)"
                      : "var(--mui-palette-error-main)",
              }}
            >
              <UsdAmountBlock amount={Math.abs(pnl24h || 0)} /> ({Number(agent.pnl_24h_rel) / 100}
              %)
            </span>
          ) : (
            <Skeleton width={80} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
        <TableCell align="right" variant="number">
          {pnl7d !== null ? (
            <span
              style={{
                color:
                  pnl7d === 0
                    ? ""
                    : pnl7d > 0
                      ? "var(--mui-palette-success-main)"
                      : "var(--mui-palette-error-main)",
              }}
            >
              <UsdAmountBlock amount={Math.abs(pnl7d || 0)} /> ({Number(agent.pnl_7d_rel) / 100}
              %)
            </span>
          ) : (
            <Skeleton width={80} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
        <TableCell align="right" variant="number">
          {pnl30d !== null ? (
            <span
              style={{
                color:
                  pnl30d === 0
                    ? ""
                    : pnl30d > 0
                      ? "var(--mui-palette-success-main)"
                      : "var(--mui-palette-error-main)",
              }}
            >
              <UsdAmountBlock amount={Math.abs(pnl30d || 0)} /> ({Number(agent.pnl_30d_rel) / 100}
              %)
            </span>
          ) : (
            <Skeleton width={80} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
        <TableCell align="right" variant="number">
          {pnlAllTime !== null ? (
            <span
              style={{
                color:
                  pnlAllTime === 0
                    ? ""
                    : pnlAllTime > 0
                      ? "var(--mui-palette-success-main)"
                      : "var(--mui-palette-error-main)",
              }}
            >
              <UsdAmountBlock amount={Math.abs(pnlAllTime || 0)} /> (
              {Number(agent.pnl_all_time_rel) / 100}
              %)
            </span>
          ) : (
            <Skeleton width={80} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
        {/* <TableCell align="right" variant="number">
          {agentInfo?.Rebalancings?.length || 0}
        </TableCell>
        <TableCell align="right" variant="number">
          {formatRelative(agent.created_at_ts)}
        </TableCell> */}
        <TableCell align="right" variant="number">
          {agentInfo ? (
            <CloneAgentButton
              onClick={() => {
                setOpen(true)
              }}
            />
          ) : (
            <Skeleton width={80} sx={{ display: "inline-block" }} />
          )}
        </TableCell>
      </TableRow>

      {open && <SpawnAgentDialog open={open} onClose={() => setOpen(false)} prefill={agentInfo} />}
    </>
  )
}
